import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const ItAssetListLayer = () => {
    const [itAssets, setItAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [perPage, setPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
    });

    // View modal state
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewingAsset, setViewingAsset] = useState(null);

    // Edit modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAsset, setEditingAsset] = useState(null);
    const [editFormData, setEditFormData] = useState({});
    const [editPicture, setEditPicture] = useState(null);
    const [editPicturePreview, setEditPicturePreview] = useState('');
    const [editError, setEditError] = useState('');
    const [editValidationErrors, setEditValidationErrors] = useState({});
    const [isUpdating, setIsUpdating] = useState(false);

    const fetchItAssets = async (page = 1) => {
        try {
            setLoading(true);
            setError('');

            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            if (!apiUrl || !token) {
                throw new Error('Missing API URL or authentication token');
            }

            const params = new URLSearchParams({
                per_page: perPage,
                page: page,
            });

            if (searchQuery) {
                params.append('search', searchQuery);
            }

            const response = await fetch(`${apiUrl}/it-assets?${params}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch IT assets: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setItAssets(data.data.data || data.data);
                setPagination(data.pagination);
            } else {
                throw new Error(data.message || 'Failed to fetch IT assets');
            }
        } catch (err) {
            console.error(err);
            setError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewClick = async (assetId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/it-assets/${assetId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setViewingAsset(data.data);
                setShowViewModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load IT asset details');
        }
    };

    const handleEditClick = async (assetId) => {
        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/it-assets/${assetId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                setEditingAsset(data.data);
                setEditFormData({
                    asset_id: data.data.asset_id || '',
                    brand_model: data.data.brand_model || '',
                    date_acquired: data.data.date_acquired ? data.data.date_acquired.split('T')[0] : '',
                    department: data.data.department || '',
                    category: data.data.category || '',
                    location: data.data.location || '',
                    condition: data.data.condition || 'good',
                    description: data.data.description || '',
                    assigned_user_department: data.data.assigned_user_department || '',
                    asset_owner: data.data.asset_owner || '',
                    remarks: data.data.remarks || '',
                    serial_number: data.data.serial_number || '',
                    supplier_vendor: data.data.supplier_vendor || '',
                });
                setEditPicture(null);
                setEditPicturePreview(data.data.picture_url || '');
                setEditError('');
                setEditValidationErrors({});
                setShowEditModal(true);
            }
        } catch (err) {
            console.error(err);
            alert('Failed to load IT asset details');
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        if (editValidationErrors[name]) {
            setEditValidationErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleEditPictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditPicture(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setEditPicturePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditError('');
        setEditValidationErrors({});

        const apiUrl = process.env.REACT_APP_API_URL;
        const token = localStorage.getItem('authToken');

        try {
            setIsUpdating(true);

            // Use FormData if there's a new picture, otherwise use JSON
            let response;
            if (editPicture) {
                // If uploading a new picture, use FormData with POST and _method
                const formData = new FormData();
                
                // Append all form fields
                Object.keys(editFormData).forEach(key => {
                    if (editFormData[key] !== null && editFormData[key] !== undefined && key !== 'picture' && key !== 'picture_url') {
                        formData.append(key, editFormData[key]);
                    }
                });

                // Append the new picture
                formData.append('picture', editPicture);
                formData.append('_method', 'PUT');

                response = await fetch(`${apiUrl}/it-assets/${editingAsset.id}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                });
            } else {
                // No new picture, just update other fields with JSON
                const updateData = { ...editFormData };
                delete updateData.picture;
                delete updateData.picture_url;

                response = await fetch(`${apiUrl}/it-assets/${editingAsset.id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify(updateData),
                });
            }

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setEditValidationErrors(data.errors);
                }
                throw new Error(data.message || 'Failed to update IT asset');
            }

            if (data.success) {
                setShowEditModal(false);
                fetchItAssets(pagination.current_page);
                alert('IT asset updated successfully!');
            }
        } catch (err) {
            console.error(err);
            setEditError(err instanceof Error ? err.message : 'Unexpected error');
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = async (assetId, assetIdName) => {
        if (!window.confirm(`Are you sure you want to delete the IT asset "${assetIdName}"?`)) {
            return;
        }

        try {
            const apiUrl = process.env.REACT_APP_API_URL;
            const token = localStorage.getItem('authToken');

            const response = await fetch(`${apiUrl}/it-assets/${assetId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (data.success) {
                fetchItAssets(pagination.current_page);
                alert('IT asset deleted successfully!');
            } else {
                alert(data.message || 'Failed to delete IT asset');
            }
        } catch (err) {
            console.error(err);
            alert('Failed to delete IT asset');
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchItAssets(1);
    };

    useEffect(() => {
        fetchItAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perPage]);

    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                if (showViewModal) {
                    setShowViewModal(false);
                } else if (showEditModal) {
                    setShowEditModal(false);
                }
            }
        };

        document.addEventListener('keydown', handleEscapeKey);
        return () => document.removeEventListener('keydown', handleEscapeKey);
    }, [showViewModal, showEditModal]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getConditionBadge = (condition) => {
        const badges = {
            excellent: 'bg-success-focus text-success-600 border border-success-main',
            good: 'bg-info-focus text-info-600 border border-info-main',
            fair: 'bg-warning-focus text-warning-600 border border-warning-main',
            poor: 'bg-danger-focus text-danger-600 border border-danger-main',
            damaged: 'bg-neutral-200 text-neutral-600 border border-neutral-400'
        };
        return badges[condition] || badges.good;
    };

    const renderPaginationButtons = () => {
        const buttons = [];
        const { current_page, last_page } = pagination;

        for (let i = 1; i <= last_page; i++) {
            buttons.push(
                <li className="page-item" key={i}>
                    <button
                        className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                            current_page === i
                                ? 'bg-primary-600 text-white'
                                : 'bg-neutral-200 text-secondary-light'
                        }`}
                        onClick={() => fetchItAssets(i)}
                    >
                        {i}
                    </button>
                </li>
            );
        }
        return buttons;
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                <div className="d-flex align-items-center flex-wrap gap-3">
                    <span className="text-md fw-medium text-secondary-light mb-0">Show</span>
                    <select
                        className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
                        value={perPage}
                        onChange={(e) => setPerPage(Number(e.target.value))}
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </select>
                    <form className="navbar-search" onSubmit={handleSearch}>
                        <input
                            type="text"
                            className="bg-base h-40-px w-auto"
                            name="search"
                            placeholder="Search IT assets..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Icon icon="ion:search-outline" className="icon" />
                    </form>
                </div>
                <Link
                    to="/register-it-asset"
                    className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
                >
                    <Icon
                        icon="ic:baseline-plus"
                        className="icon text-xl line-height-1"
                    />
                    Add New IT Asset
                </Link>
            </div>
            <div className="card-body p-24">
                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive scroll-sm">
                            <table className="table bordered-table sm-table mb-0">
                                <thead>
                                    <tr>
                                        <th scope="col">S.L</th>
                                        <th scope="col">Asset ID</th>
                                        <th scope="col">Brand / Model</th>
                                        <th scope="col">Category</th>
                                        <th scope="col">Serial Number</th>
                                        <th scope="col">Description</th>
                                        <th scope="col">Assigned User / Department</th>
                                        <th scope="col">Date Acquired</th>
                                        <th scope="col">Supplier / Vendor</th>
                                        <th scope="col">Department</th>
                                        <th scope="col">Asset Owner</th>
                                        <th scope="col">Remarks</th>
                                        <th scope="col">Location</th>
                                        <th scope="col" className="text-center">
                                            Condition
                                        </th>
                                        <th scope="col" className="text-center">
                                            Action
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itAssets.length === 0 ? (
                                        <tr>
                                            <td colSpan="15" className="text-center py-4">
                                                No IT assets found
                                            </td>
                                        </tr>
                                    ) : (
                                        itAssets.map((asset, index) => (
                                            <tr key={asset.id}>
                                                <td>
                                                    {(pagination.current_page - 1) * perPage + index + 1}
                                                </td>
                                                <td>
                                                    <span className="badge bg-primary-50 text-primary-600 px-12 py-4 radius-4 fw-medium text-sm">
                                                        {asset.asset_id}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.brand_model}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.category || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.serial_number || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light" title={asset.description || ''}>
                                                        {asset.description ? (asset.description.length > 50 ? asset.description.substring(0, 50) + '...' : asset.description) : 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.assigned_user_department || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(asset.date_acquired)}</td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.supplier_vendor || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.department || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.asset_owner || 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light" title={asset.remarks || ''}>
                                                        {asset.remarks ? (asset.remarks.length > 50 ? asset.remarks.substring(0, 50) + '...' : asset.remarks) : 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-md mb-0 fw-normal text-secondary-light">
                                                        {asset.location || 'N/A'}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <span
                                                        className={`${getConditionBadge(
                                                            asset.condition
                                                        )} px-24 py-4 radius-4 fw-medium text-sm text-capitalize`}
                                                    >
                                                        {asset.condition}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="d-flex align-items-center gap-10 justify-content-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewClick(asset.id)}
                                                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            title="View Details"
                                                        >
                                                            <Icon
                                                                icon="majesticons:eye-line"
                                                                className="icon text-xl"
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleEditClick(asset.id)}
                                                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            title="Edit IT Asset"
                                                        >
                                                            <Icon icon="lucide:edit" className="menu-icon" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDelete(asset.id, asset.asset_id)}
                                                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                            title="Delete IT Asset"
                                                        >
                                                            <Icon
                                                                icon="fluent:delete-24-regular"
                                                                className="menu-icon"
                                                            />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                            <span>
                                Showing {pagination.from || 0} to {pagination.to || 0} of{' '}
                                {pagination.total} entries
                            </span>
                            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
                                <li className="page-item">
                                    <button
                                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                        onClick={() => fetchItAssets(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    >
                                        <Icon icon="ep:d-arrow-left" className="" />
                                    </button>
                                </li>
                                {renderPaginationButtons()}
                                <li className="page-item">
                                    <button
                                        className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                                        onClick={() => fetchItAssets(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    >
                                        <Icon icon="ep:d-arrow-right" className="" />
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </>
                )}
            </div>

            {/* View IT Asset Modal */}
            {showViewModal && viewingAsset && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">IT Asset Details</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowViewModal(false)}
                                />
                            </div>
                            <div className="modal-body">
                                {viewingAsset.picture_url && (
                                    <div className="text-center mb-4">
                                        <img
                                            src={viewingAsset.picture_url}
                                            alt={viewingAsset.brand_model}
                                            className="img-fluid rounded"
                                            style={{ maxHeight: '400px', objectFit: 'contain' }}
                                        />
                                    </div>
                                )}
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <strong>Asset ID:</strong> {viewingAsset.asset_id}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Brand / Model:</strong> {viewingAsset.brand_model}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Category:</strong> {viewingAsset.category || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Serial Number:</strong> {viewingAsset.serial_number || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Department:</strong> {viewingAsset.department || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Location:</strong> {viewingAsset.location || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Assigned User / Department:</strong> {viewingAsset.assigned_user_department || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Asset Owner:</strong> {viewingAsset.asset_owner || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Supplier / Vendor:</strong> {viewingAsset.supplier_vendor || 'N/A'}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Date Acquired:</strong> {formatDate(viewingAsset.date_acquired)}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <strong>Condition:</strong>{' '}
                                        <span className={`badge ${getConditionBadge(viewingAsset.condition)} text-capitalize`}>
                                            {viewingAsset.condition}
                                        </span>
                                    </div>
                                    <div className="col-12 mb-3">
                                        <strong>Description:</strong> {viewingAsset.description || 'N/A'}
                                    </div>
                                    <div className="col-12 mb-3">
                                        <strong>Remarks:</strong> {viewingAsset.remarks || 'N/A'}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowViewModal(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit IT Asset Modal */}
            {showEditModal && editingAsset && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit IT Asset</h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={isUpdating}
                                />
                            </div>
                            <form onSubmit={handleEditSubmit} noValidate>
                                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                    {editError && (
                                        <div className="alert alert-danger" role="alert">
                                            {editError}
                                        </div>
                                    )}
                                    <div className="row">
                                        {/* Row 1: Asset ID / Tag Number | Category / Type */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Asset ID / Tag Number *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.asset_id ? 'is-invalid' : ''}`}
                                                name="asset_id"
                                                value={editFormData.asset_id || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            />
                                            {editValidationErrors.asset_id && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.asset_id[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Category / Type *</label>
                                            <select
                                                className={`form-control form-select ${editValidationErrors.category ? 'is-invalid' : ''}`}
                                                name="category"
                                                value={editFormData.category || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                <option value="Computing Devices">Computing Devices</option>
                                                <option value="Peripherals & Accessories">Peripherals & Accessories</option>
                                                <option value="Networking & Power Equipment">Networking & Power Equipment</option>
                                                <option value="Software & Licenses">Software & Licenses</option>
                                            </select>
                                            {editValidationErrors.category && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.category[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 2: Brand / Model | Serial Number */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Brand / Model *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.brand_model ? 'is-invalid' : ''}`}
                                                name="brand_model"
                                                value={editFormData.brand_model || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            />
                                            {editValidationErrors.brand_model && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.brand_model[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Serial Number</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.serial_number ? 'is-invalid' : ''}`}
                                                name="serial_number"
                                                value={editFormData.serial_number || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.serial_number && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.serial_number[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 3: Specifications / Description (full width) */}
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Specifications / Description</label>
                                            <textarea
                                                className={`form-control ${editValidationErrors.description ? 'is-invalid' : ''}`}
                                                name="description"
                                                rows="3"
                                                value={editFormData.description || ''}
                                                onChange={handleEditInputChange}
                                            />
                                            {editValidationErrors.description && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.description[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 4: Assigned User / Department | Location */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Assigned User / Department</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="assigned_user_department"
                                                value={editFormData.assigned_user_department || ''}
                                                onChange={handleEditInputChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Location *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.location ? 'is-invalid' : ''}`}
                                                name="location"
                                                value={editFormData.location || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            />
                                            {editValidationErrors.location && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.location[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 5: Date Acquired / Purchase Date | Supplier / Vendor */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Date Acquired / Purchase Date *</label>
                                            <input
                                                type="date"
                                                className={`form-control ${editValidationErrors.date_acquired ? 'is-invalid' : ''}`}
                                                name="date_acquired"
                                                value={editFormData.date_acquired || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            />
                                            {editValidationErrors.date_acquired && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.date_acquired[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Supplier / Vendor</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="supplier_vendor"
                                                value={editFormData.supplier_vendor || ''}
                                                onChange={handleEditInputChange}
                                            />
                                        </div>

                                        {/* Row 6: Department | Condition / Status */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Department *</label>
                                            <input
                                                type="text"
                                                className={`form-control ${editValidationErrors.department ? 'is-invalid' : ''}`}
                                                name="department"
                                                value={editFormData.department || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            />
                                            {editValidationErrors.department && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.department[0]}
                                                </div>
                                            )}
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Condition / Status *</label>
                                            <select
                                                className={`form-control form-select ${editValidationErrors.condition ? 'is-invalid' : ''}`}
                                                name="condition"
                                                value={editFormData.condition || ''}
                                                onChange={handleEditInputChange}
                                                required
                                            >
                                                <option value="excellent">Excellent</option>
                                                <option value="good">Good</option>
                                                <option value="fair">Fair</option>
                                                <option value="poor">Poor</option>
                                                <option value="damaged">Damaged</option>
                                            </select>
                                            {editValidationErrors.condition && (
                                                <div className="invalid-feedback d-block">
                                                    {editValidationErrors.condition[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 7: Asset Owner (Custodian) | Picture */}
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Asset Owner (Custodian)</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="asset_owner"
                                                value={editFormData.asset_owner || ''}
                                                onChange={handleEditInputChange}
                                            />
                                        </div>

                                        <div className="col-md-6 mb-3">
                                            <label className="form-label fw-semibold">Picture</label>
                                            <input
                                                type="file"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={handleEditPictureChange}
                                            />
                                            {editPicturePreview && (
                                                <div className="mt-2">
                                                    <img
                                                        src={editPicturePreview}
                                                        alt="Preview"
                                                        className="img-thumbnail"
                                                        style={{ maxWidth: '200px', maxHeight: '200px' }}
                                                    />
                                                </div>
                                            )}
                                            {editValidationErrors.picture && (
                                                <div className="text-danger mt-1">
                                                    {editValidationErrors.picture[0]}
                                                </div>
                                            )}
                                        </div>

                                        {/* Row 8: Remarks / Notes (full width) */}
                                        <div className="col-12 mb-3">
                                            <label className="form-label fw-semibold">Remarks / Notes</label>
                                            <textarea
                                                className="form-control"
                                                name="remarks"
                                                rows="3"
                                                value={editFormData.remarks || ''}
                                                onChange={handleEditInputChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setShowEditModal(false)}
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isUpdating}
                                    >
                                        {isUpdating ? 'Updating...' : 'Update IT Asset'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ItAssetListLayer;

